import PropTypes from 'prop-types';
import React from 'react';
import ReactDisqusComments from 'react-disqus-comments';
import styled from 'styled-components';
import { Helmet } from 'react-helmet';
import { graphql, Link } from 'gatsby';
import 'prismjs/themes/prism-solarizedlight.css';
import 'katex/dist/katex.min.css';

import Clear from '../components/clear';
import StyledLink from '../components/styledLink';
import TagLink from '../components/tagLink';
import colors from '../utils/colors';

const PostBody = styled.div`
  div#post-content {
    font-family: 'Open Sans', sans-serif;
    margin-top: 40px;
    margin-bottom: 40px;
    pre,
    code {
      font-family: 'Inconsolata', monospace;
      line-height: 20px;
    }
    p,
    li {
      line-height: 25px;
      color: ${colors.base2()};
    }
    center {
      margin-top: -15px;
      color: ${colors.base2()};
    }
    h2,
    h3 {
      color: ${colors.base1()};
      text-transform: uppercase;
      margin: 32px 0 8px 0;
    }
    a {
      color: ${colors.accent1()};
      transition: all 0.5s;
      &:hover {
        background-color: ${colors.accent1(0.1)};
      }
    }
    .gatsby-resp-image-figure {
      margin: 0;
    }
    .gatsby-resp-image-figcaption {
      text-align: center;
      font-family: 'Open Sans', sans-serif;
      color: ${colors.base2()};
      font-style: italic;
    }
  }
  margin: 0 auto 60px auto;
  position: relative;
  max-width: 675px;
`;

const PostTitle = styled.h1`
  margin-bottom: 5px;
  padding: 20px 0px 20px 0px;
  color: ${colors.base1()};
  text-transform: uppercase;
`;

const PostSubtitle = styled.h4`
  margin-top: 0px;
  margin-bottom: 5px;
  color: ${colors.base3()};
  text-decoration: none;
  font-weight: normal;
  text-transform: uppercase;
`;

const PostLink = styled.div`
  width: 40%;
  text-transform: uppercase;
  font-family: 'Josefin Sans', sans-serif;
`;

const PrevPostLink = styled(PostLink)`
  float: left;
  text-align: left !important;
  div {
    text-align: left !important;
  }
`;

const NextPostLink = styled(PostLink)`
  float: right;
  text-align: right !important;
  div {
    text-align: right !important;
  }
`;


class PostTemplate extends React.Component {
  componentDidMount() {
    this.node.style.opacity = 0;
    window.requestAnimationFrame(() => {
      this.node.style.transition = 'opacity 1000ms ease-out';
      this.node.style.opacity = 1;
    });
    this.activateScripts();
  }

  // Markdown HTML is injected with dangerouslySetInnerHTML, and the browser
  // does not run <script> tags that arrive that way. Re-creating each script
  // node makes the browser execute it, which is what lets a post embed an
  // interactive widget. Posts without scripts (the older ones) are untouched:
  // the loop finds nothing. Widgets guard their own re-initialisation, so this
  // is safe to run again on client-side navigation back to a post.
  activateScripts() {
    const content = this.node && this.node.querySelector('#post-content');
    if (!content) return;
    content.querySelectorAll('script').forEach((old) => {
      const fresh = document.createElement('script');
      if (old.src) {
        fresh.src = old.src;
      } else {
        fresh.textContent = old.textContent;
      }
      old.parentNode.replaceChild(fresh, old);
    });
  }

  render() {
    const {
      data: { prevPost, currPost, nextPost },
    } = this.props;
    const tags = [];

    const dateOptions = {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };
    const formattedDate = new Date(currPost.frontmatter.date_created).toLocaleDateString(
      'en-US',
      dateOptions,
    );
    
    currPost.frontmatter.tags.forEach((tagName) => {
      tags.push(
        <TagLink key={tagName}>
          {tagName}
        </TagLink>,
      );
    });

    return (
      <PostBody
        ref={(node) => {
          this.node = node;
        }}
      >
        <Helmet>
          <title>{`${currPost.frontmatter.title} | Kartikeya Sharma`}</title>
        </Helmet>
        <PostTitle>{currPost.frontmatter.title}</PostTitle>
        <PostSubtitle>{formattedDate}</PostSubtitle>
        {tags}
        <div id="post-content" dangerouslySetInnerHTML={{ __html: currPost.html }} />
        <div style={{ marginTop: '40px', paddingTop: '20px', borderTop: '1px solid #dae4ed' }}>
          <StyledLink to="/blog">&larr; Back to all articles</StyledLink>
        </div>
        <Clear />
      </PostBody>
    );
  }
}

const postType = PropTypes.shape({
  html: PropTypes.string,
  headings: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string.isRequired,
      depth: PropTypes.number.isRequired,
    }),
  ).isRequired,
  frontmatter: PropTypes.shape({
    date_created: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    title: PropTypes.string.isRequired,
    src: PropTypes.string.isRequired
  }).isRequired,
});

PostTemplate.propTypes = {
  pageContext: PropTypes.shape({
    prevPostPath: PropTypes.string,
    currPostPath: PropTypes.string.isRequired,
    nextPostPath: PropTypes.string,
  }).isRequired,
  data: PropTypes.shape({
    prevPost: postType,
    currPost: postType.isRequired,
    nextPost: postType,
  }).isRequired,
};

export default PostTemplate;
export const pageQuery = graphql`
  query($prevPostPath: String, $currPostPath: String!, $nextPostPath: String) {
    prevPost: markdownRemark(frontmatter: { path: { eq: $prevPostPath } }) {
      ...postFields
    }
    currPost: markdownRemark(frontmatter: { path: { eq: $currPostPath } }) {
      ...postFields
    }
    nextPost: markdownRemark(frontmatter: { path: { eq: $nextPostPath } }) {
      ...postFields
    }
  }
  fragment postFields on MarkdownRemark {
    html
    headings {
      value
      depth
    }
    frontmatter {
      date_created
      path
      tags
      title
    }
  }
`;
